import { componentOnReady } from '@familyjs/core/components';
import React from 'react';

import { FmlRouterOutletInner } from '../components/inner-proxies';
import { FmlLifeCycleContext } from '../contexts/FmlLifeCycleContext';
import type { RouteInfo } from '../models';

import { StackContext } from './StackContext';

interface OutletPageManagerProps {
  className?: string;
  forwardedRef?: React.ForwardedRef<HTMLFmlRouterOutletElement>;
  routeInfo?: RouteInfo;
  StackManager: any; // TODO(FW-2959): type
}

export class OutletPageManager extends React.Component<OutletPageManagerProps> {
  fmlLifeCycleContext!: React.ContextType<typeof FmlLifeCycleContext>;
  context!: React.ContextType<typeof StackContext>;
  fmlRouterOutlet: HTMLFmlRouterOutletElement | undefined;
  outletIsReady: boolean;

  constructor(props: OutletPageManagerProps) {
    super(props);

    this.outletIsReady = false;

    /**
     * This binds the scope of the following methods to the class scope.
     * The `.bind` method returns a new function, so we need to assign it
     * in the constructor rather than when adding or removing the listeners
     * to avoid creating a new function.
     */
    this.fmlViewWillEnterHandler = this.fmlViewWillEnterHandler.bind(this);
    this.fmlViewDidEnterHandler = this.fmlViewDidEnterHandler.bind(this);
    this.fmlViewWillLeaveHandler = this.fmlViewWillLeaveHandler.bind(this);
    this.fmlViewDidLeaveHandler = this.fmlViewDidLeaveHandler.bind(this);
  }

  componentDidMount() {
    if (this.fmlRouterOutlet) {
      /**
       * This avoids multiple raf calls
       * when React unmounts + remounts components.
       */
      if (!this.outletIsReady) {
        componentOnReady(this.fmlRouterOutlet, () => {
          this.outletIsReady = true;
          this.context.registerFmlPage(this.fmlRouterOutlet!, this.props.routeInfo!);
        });
      }

      this.fmlRouterOutlet.addEventListener('fmlViewWillEnter', this.fmlViewWillEnterHandler);
      this.fmlRouterOutlet.addEventListener('fmlViewDidEnter', this.fmlViewDidEnterHandler);
      this.fmlRouterOutlet.addEventListener('fmlViewWillLeave', this.fmlViewWillLeaveHandler);
      this.fmlRouterOutlet.addEventListener('fmlViewDidLeave', this.fmlViewDidLeaveHandler);
    }
  }

  componentWillUnmount() {
    if (this.fmlRouterOutlet) {
      this.fmlRouterOutlet.removeEventListener('fmlViewWillEnter', this.fmlViewWillEnterHandler);
      this.fmlRouterOutlet.removeEventListener('fmlViewDidEnter', this.fmlViewDidEnterHandler);
      this.fmlRouterOutlet.removeEventListener('fmlViewWillLeave', this.fmlViewWillLeaveHandler);
      this.fmlRouterOutlet.removeEventListener('fmlViewDidLeave', this.fmlViewDidLeaveHandler);
    }
  }

  fmlViewWillEnterHandler() {
    this.fmlLifeCycleContext.fmlViewWillEnter();
  }

  fmlViewDidEnterHandler() {
    this.fmlLifeCycleContext.fmlViewDidEnter();
  }

  fmlViewWillLeaveHandler() {
    this.fmlLifeCycleContext.fmlViewWillLeave();
  }

  fmlViewDidLeaveHandler() {
    this.fmlLifeCycleContext.fmlViewDidLeave();
  }

  render() {
    const { StackManager, children, routeInfo, ...props } = this.props;
    return (
      <FmlLifeCycleContext.Consumer>
        {(context) => {
          this.fmlLifeCycleContext = context;
          return (
            <StackManager routeInfo={routeInfo}>
              <FmlRouterOutletInner
                setRef={(val: HTMLFmlRouterOutletElement) => (this.fmlRouterOutlet = val)}
                {...props}
              >
                {children}
              </FmlRouterOutletInner>
            </StackManager>
          );
        }}
      </FmlLifeCycleContext.Consumer>
    );
  }

  static get contextType() {
    return StackContext;
  }
}
export default OutletPageManager;
